import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { useToast } from './Toast';

export function RegistrantsPanel({
  workshopId,
  workshopTitle,
}: {
  workshopId: string;
  workshopTitle: string;
}) {
  const toast = useToast();
  const query = useQuery({
    queryKey: ['admin', 'registrations', workshopId],
    queryFn: () => api.admin.listRegistrations(workshopId),
  });

  const handleExport = async () => {
    const safeTitle = workshopTitle
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40);
    try {
      await api.admin.downloadRegistrationsCsv(workshopId, `${safeTitle}-registrations.csv`);
    } catch (err) {
      toast.error('CSV export failed', err instanceof ApiError ? err.message : String(err));
    }
  };

  const registrations = query.data?.registrations ?? [];

  return (
    <section className="rounded-2xl border border-line bg-surface shadow-card">
      <div className="flex items-center justify-between border-b border-line px-5 py-4">
        <div>
          <h3 className="text-sm font-semibold tracking-tight">Registrations</h3>
          <p className="mt-0.5 text-xs text-ink-muted">
            {query.isLoading
              ? 'Loading…'
              : registrations.length === 0
                ? 'No one has registered yet.'
                : `${registrations.length} ${registrations.length === 1 ? 'person' : 'people'} registered`}
          </p>
        </div>
        <button
          type="button"
          onClick={handleExport}
          className="btn-secondary"
          disabled={registrations.length === 0}
        >
          Export CSV
        </button>
      </div>

      {registrations.length === 0 ? null : (
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-canvas/95 text-[11px] uppercase tracking-wider text-ink-subtle">
              <tr>
                <th className="px-5 py-2 text-left font-semibold">Name</th>
                <th className="px-5 py-2 text-left font-semibold">Email</th>
                <th className="px-5 py-2 text-left font-semibold">Registered</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {registrations.map((r) => (
                <tr key={r.id} className="hover:bg-canvas/60">
                  <td className="px-5 py-2.5">{r.name}</td>
                  <td className="px-5 py-2.5 text-ink-muted">
                    <a
                      href={`mailto:${r.email}`}
                      className="hover:text-accent hover:underline"
                    >
                      {r.email}
                    </a>
                  </td>
                  <td className="px-5 py-2.5 text-ink-muted whitespace-nowrap">
                    {formatDateTime(r.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
